/* Värld: slow auto-glide for the home page artists strip. Pauses on hover, touch, focus, search, off screen and for reduced motion. */
if (!customElements.get('artist-glide')) {
  class ArtistGlide extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('.artist-glide__scroller');
      this.track = this.querySelector('.artist-glide__track');
      this.originals = Array.from(this.track.children);
      if (this.originals.length < 2) return;
      this.speed = parseFloat(this.dataset.speed || '28'); // px per second
      this.originals.forEach((el) => {
        const c = el.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.setAttribute('inert', '');
        c.removeAttribute('data-shopify-editor-block');
        this.track.appendChild(c);
      });
      this.firstClone = this.track.children[this.originals.length];
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.section = this.closest('.ag-section');
      this.hover = false;
      this.focus = false;
      this.lastTouch = 0;
      this.holdUntil = 0;
      this.visible = false;
      this.pos = 0;
      this.raf = null;
      this.last = 0;
      this.rampStart = 0;
      this.wasRunning = false;

      this.scroller.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.hover = true; });
      this.scroller.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') this.hover = false; });
      // touch: no sticky flag, just a very short hold that every finger move or scroll extends
      const touchHold = () => { this.lastTouch = performance.now(); this.hold(400); };
      ['touchstart', 'touchmove', 'touchend', 'touchcancel'].forEach((ev) => this.addEventListener(ev, touchHold, { passive: true }));
      this.scroller.addEventListener('focusin', (e) => { if (e.target.matches(':focus-visible')) this.focus = true; });
      this.scroller.addEventListener('focusout', () => { this.focus = false; });
      this.scroller.addEventListener('scroll', () => this.onScroll(), { passive: true });

      new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.start();
      }).observe(this);
    }

    loopWidth() { return this.firstClone.offsetLeft - this.originals[0].offsetLeft; }
    hold(ms) { this.holdUntil = Math.max(this.holdUntil, performance.now() + ms); }

    running() {
      return this.visible && !this.hover && !this.focus && !document.hidden &&
        !(this.section && this.section.classList.contains('ag-searching')) &&
        !this.reduced.matches && performance.now() > this.holdUntil;
    }

    onScroll() {
      const sl = this.scroller.scrollLeft;
      if (Math.abs(sl - this.pos) < 2) return; // our own glide write
      const lw = this.loopWidth();
      let x = sl;
      if (performance.now() - this.lastTouch > 300) {
        if (x >= lw) x -= lw;
        else if (x <= 0) x += lw;
        if (x !== sl) this.scroller.scrollLeft = x;
      }
      this.pos = x;
      this.hold(400);
    }

    start() {
      if (this.raf) return;
      this.last = performance.now();
      const tick = (t) => {
        if (!this.visible) { this.raf = null; return; }
        const dt = Math.min(t - this.last, 100);
        this.last = t;
        const run = this.running();
        if (run) {
          if (!this.wasRunning) { this.pos = this.scroller.scrollLeft; this.rampStart = t; }
          const ease = Math.min(1, (t - this.rampStart) / 500); // gentle start instead of a jolt
          this.pos += (this.speed * ease * dt) / 1000;
          const lw = this.loopWidth();
          if (this.pos >= lw) this.pos -= lw;
          this.scroller.scrollLeft = this.pos;
        }
        this.wasRunning = run;
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }
  }
  customElements.define('artist-glide', ArtistGlide);
}

/* 'Find an artist': filters the small artist list embedded in the page, no server calls. */
if (!customElements.get('artist-search')) {
  class ArtistSearch extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('.ag-search__toggle');
      this.panel = this.querySelector('.ag-search__panel');
      this.input = this.querySelector('.ag-search__input');
      this.list = this.querySelector('.ag-search__results');
      this.section = this.closest('.ag-section');
      this.data = null;
      this.toggle.setAttribute('aria-label', 'Find an artist');
      this.toggle.addEventListener('click', () => this.open());
      this.input.addEventListener('input', () => this.render());
      this.input.addEventListener('keydown', (e) => this.key(e));
      document.addEventListener('click', (e) => { if (this.isOpen && !this.contains(e.target)) this.close(); });
    }
    norm(s) { return (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim(); }
    load() {
      if (this.data) return;
      try { this.data = JSON.parse(this.querySelector('.ag-search__data').textContent).filter(Boolean); } catch (e) { this.data = []; }
      this.data.forEach((a) => { a.k = this.norm(a.n); });
    }
    open() {
      this.load();
      // the open bar sits over the small header, so give it the section's own background
      if (this.section) this.style.setProperty('--ag-bg', getComputedStyle(this.section).backgroundColor);
      this.isOpen = true;
      this.classList.add('is-open');
      this.toggle.hidden = true;
      this.panel.hidden = false;
      this.section && this.section.classList.add('ag-searching');
      this.input.focus();
      this.render();
    }
    close() {
      this.isOpen = false;
      this.classList.remove('is-open');
      this.panel.hidden = true;
      this.toggle.hidden = false;
      this.input.value = '';
      this.list.innerHTML = '';
      this.section && this.section.classList.remove('ag-searching');
    }
    render() {
      const q = this.norm(this.input.value);
      this.active = -1;
      if (!q) { this.list.innerHTML = ''; return; }
      const starts = [], has = [];
      this.data.forEach((a) => {
        if (a.k.startsWith(q) || a.k.split(/\s+/).some((w) => w.startsWith(q))) starts.push(a);
        else if (a.k.includes(q)) has.push(a);
      });
      const hits = starts.concat(has).slice(0, 6);
      this.list.innerHTML = hits.length
        ? hits.map((a) => '<li><a href="' + encodeURI(a.u) + '"><span class="ag-search__n"></span>' + (a.c ? '<span class="ag-search__c"></span>' : '') + '</a></li>').join('')
        : '<li class="ag-search__none">No artist found</li>';
      this.list.querySelectorAll('a').forEach((el, i) => {
        el.querySelector('.ag-search__n').textContent = hits[i].n;
        const c = el.querySelector('.ag-search__c'); if (c) c.textContent = hits[i].c;
      });
    }
    key(e) {
      const links = Array.from(this.list.querySelectorAll('a'));
      if (e.key === 'Escape') { this.close(); this.toggle.focus(); return; }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!links.length) return;
        this.active = (this.active + (e.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length;
        links.forEach((l, i) => l.classList.toggle('is-active', i === this.active));
      }
      if (e.key === 'Enter' && links.length) { e.preventDefault(); window.location.href = links[Math.max(this.active, 0)].href; }
    }
  }
  customElements.define('artist-search', ArtistSearch);
}
